import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, retry, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationResponse, CreateAccountRequest, User, UserProfile } from './../dto';
import { AbstractService } from './abstractservice';
import { AuthService } from './auth.service';
import { FeatureFlagService } from './feature-flag.service';
import { UnifiedRepositoryGatewayService } from './repository/unified-repository-gateway.service';
import { SignalService } from './signal.service';


@Injectable({
    providedIn: 'root'
})
export class UserService extends AbstractService {

    public activeUser$: BehaviorSubject<User> = this.authService.authenticatedUser$;
    // equal: () => false, not the default reference check - callers (this service's own
    // updateUserProfile, CustomizationService's equip-with-revert flow) mutate the held
    // UserProfile instance in place and re-notify with the same reference, same as the
    // BehaviorSubject this replaced; UserProfile is a real class instance (see dto.ts's map()/
    // cacheNestedElements()), so rebuilding a fresh object via spread on every write - the usual
    // fix for this - would silently drop its prototype instead.
    private readonly activeUserProfileState: WritableSignal<UserProfile> = signal<UserProfile>(null, { equal: () => false });
    public readonly activeUserProfileSignal: Signal<UserProfile> = this.activeUserProfileState.asReadonly();
    private userProfileFromActiveUser$: Observable<UserProfile>;

    constructor(private http: HttpClient, authService: AuthService, featureFlagService: FeatureFlagService, private notificationService: SignalService, private unifiedRepo: UnifiedRepositoryGatewayService) {
        super(authService, featureFlagService);
        this.userProfileFromActiveUser$ = this.activeUser$.pipe(
            filter(user => !!user && !!user.Username && authService.isAuthenticated()),
            distinctUntilChanged((prevUser, currUser) => prevUser.Username === currUser.Username),
            switchMap(user => this.unifiedRepo.getOrRetrieve(true, true, UserProfile, user.Username)),
            map(profileArr => profileArr?.pop()),
        );
        this.userProfileFromActiveUser$.subscribe(userProfile => this.activeUserProfileState.set(userProfile));
    }

    // For external cache updates that mutate the held UserProfile in place (e.g.
    // CustomizationService's equip-with-revert flow) and need to re-notify subscribers.
    public setActiveUserProfile(profile: UserProfile): void {
        this.activeUserProfileState.set(profile);
    }

    public updateUserCash(delta: number) {
        if (!delta) return;
        const user = this.activeUser$.getValue();
        user.Cash = (user.Cash ?? 0) + delta;
        this.activeUser$.next(user);
    }

    public checkUsername(name: string): Observable<void> {
        return this.http.get<void>(environment.apiUrl + `/user/valid/username/${name}`, this.httpOptionsAuthJson());
    }

    public checkEmail(email: string): Observable<void> {//<boolean> {
        return this.http.get<void>(environment.apiUrl + `/user/valid/email/${email}`, this.httpOptionsAuthJson());
    }

    public createUser(request: CreateAccountRequest): Observable<AuthenticationResponse> {
        return this.unifiedRepo.userRepository.createUser(request).pipe(
            tap(authR => {
                this.authService.sessionSummary = authR.SessionSummary;
                this.authService.loginResponse = authR;
                this.authService.jwtToken = authR.AuthToken;
                this.authService.authenticatedUser$.next(authR.SessionSummary.User);
            }),
            retry(2)
        );
    }

    public retrieveUserProfile(username: string): Observable<UserProfile> {
        return this.unifiedRepo.getOrRetrieve<UserProfile>(true, true, UserProfile, username).
            pipe(
                map(arr => arr[0]));
    }

    public updateUserProfile(userProfile: UserProfile): Observable<void> {
        return this.unifiedRepo.userProfileRepository.updateUserProfile(userProfile)
            .pipe(
                // map(arr => arr[0]),
                tap(() => this.activeUserProfileState.set(userProfile))
            );
    }

    public retrieveUser(username: string): Observable<User> {
        if (!username) {
            return this.unifiedRepo.retrieveSingle(true, true, User, username);
        } else {
            return this.unifiedRepo.getOrRetrieve(true, true, User, username).pipe(map(arr => arr[0]));
        }
    }

    public updateUser(user: User): Observable<void> {
        return this.unifiedRepo.userRepository.updateUser(user)
            .pipe(
                // map(arr => arr[0]),
                tap(() => this.activeUser$.next(user))
            );
    }

}
